import { Box, Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'

interface EmptyListProps {
  /** Drawn inside the ring — a sensor dial or a valve, not a generic box. */
  glyph: 'sensor' | 'valve'
  title: string
  hint: string
  actionLabel: string
  onAction: () => void
  disabled?: boolean
}

export const SensorGlyph = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22 }}>
    {/* A gauge: the dial and the needle. */}
    <circle
      cx="12"
      cy="13"
      r="7.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M12 13 L15.4 9.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 3.6 v1.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </Box>
)

export const ValveGlyph = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22 }}>
    {/* A gate valve on a pipe: body, stem and handwheel. */}
    <path
      d="M3 13 h4 M17 13 h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M7 9.4 L17 16.6 L17 9.4 L7 16.6 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 13 v-4.4 M9.4 8 h5.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </Box>
)

/**
 * The empty state for the two lists.
 *
 * An empty grid with a sentence under it reads as something that failed to
 * load. This reads as a slot waiting to be filled: a dashed frame the size of
 * the list, the thing that belongs there drawn once, and the button that puts
 * it there — which drops straight into bulk edit with the create row open, so
 * adding the first sensor is one click rather than three.
 */
const EmptyList = ({
  glyph,
  title,
  hint,
  actionLabel,
  onAction,
  disabled,
}: EmptyListProps) => {
  const isDark = useSelector(selectTheme) === mode_type.dark

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        m: '10px',
        p: '14px',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '9px',
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'border.main',
        borderRadius: '10px',
        backgroundColor: 'background.overrideDefault',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          // primary.main is #FFFFFF in dark, so the glyph is given the brand
          // green literally instead.
          color: isDark ? '#34d399' : '#004744',
          backgroundColor: isDark
            ? 'rgba(52, 211, 153, 0.12)'
            : 'rgba(0, 71, 68, 0.07)',
        }}
      >
        {glyph === 'sensor' ? <SensorGlyph /> : <ValveGlyph />}
      </Box>

      <Stack sx={{ gap: '2px' }}>
        <Typography
          sx={{ fontSize: '13px', fontWeight: 650, color: 'text.main' }}
        >
          {title}
        </Typography>
        {/* grey.600 is the same #7C7B7B in both themes and only reaches 2.7:1
          against the dark panel, short of the 4.5:1 AA needs at this size. */}
        <Typography
          sx={{
            fontSize: '11.5px',
            color: isDark ? '#9fb3bd' : 'grey.600',
            maxWidth: '34ch',
          }}
        >
          {hint}
        </Typography>
      </Stack>

      {!disabled && (
        <Button
          variant="secondary"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={onAction}
          sx={{ mt: '2px' }}
        >
          {actionLabel}
        </Button>
      )}
    </Stack>
  )
}

export default EmptyList
